/**
 * Export Audit Logs Component
 * Handle export to CSV and Excel formats
 */

import React, { useState } from "react";
import {
  HStack,
  Button,
  useToast,
  Spinner,
  useColorModeValue,
} from "@chakra-ui/react";
import { FiDownload } from "react-icons/fi";
import { AuditLogFilters, ExportFormat, TimeRangeOption } from "./types";
import auditLogsApiService from "./auditLogsApiService";
import { generateExportFilename } from "./utils";

interface ExportAuditLogsProps {
  filters: AuditLogFilters;
  timeRange?: TimeRangeOption;
  disabled?: boolean;
}

const ExportAuditLogs: React.FC<ExportAuditLogsProps> = ({
  filters,
  timeRange = "last_24_hours",
  disabled = false,
}) => {
  const [exporting, setExporting] = useState<ExportFormat | null>(null);
  const toast = useToast();

  const handleExport = async (format: ExportFormat) => {
    setExporting(format);
    try {
      const filename = generateExportFilename(format, timeRange);
      const blob = await auditLogsApiService.exportAuditLogs({
        ...filters,
        export_format: format,
      });

      auditLogsApiService.downloadFile(blob, filename);

      toast({
        title: "Export Berhasil",
        description: `Data telah di-export ke format ${format.toUpperCase()}`,
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error("Export error:", error);
      toast({
        title: "Export Gagal",
        description: "Terjadi kesalahan saat melakukan export data",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setExporting(null);
    }
  };

  return (
    <HStack spacing={2}>
      <Button
        size="sm"
        colorScheme="teal"
        variant="outline"
        leftIcon={exporting === "csv" ? <Spinner size="sm" /> : <FiDownload />}
        onClick={() => handleExport("csv")}
        isDisabled={disabled || exporting !== null}
      >
        Export CSV
      </Button>
      <Button
        size="sm"
        colorScheme="green"
        variant="outline"
        leftIcon={exporting === "excel" ? <Spinner size="sm" /> : <FiDownload />}
        onClick={() => handleExport("excel")}
        isDisabled={disabled || exporting !== null}
      >
        Export Excel
      </Button>
    </HStack>
  );
};

export default ExportAuditLogs;
